import { ChevronRight } from 'lucide-react';
import { useRef, useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { cn } from '../../../lib/utils';

interface MenuButtonProps {
  label: string;
  options: { label: string; action: () => void }[];
  onOpenChange?: (open: boolean) => void;
}

const MenuButton = ({ label, options, onOpenChange }: MenuButtonProps) => {
  const [open, setOpen] = useState(false);
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };

  const cancelClose = () => clearTimeout(closeTimeout.current);
  const scheduleClose = () => {
    closeTimeout.current = setTimeout(() => handleOpenChange(false), 100);
  };

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger
        className="flex items-center gap-1 text-sm font-semibold uppercase tracking-wide text-inherit hover:opacity-80 transition-opacity focus-visible:outline-2 focus-visible:outline-white focus-visible:outline-offset-2 rounded-sm"
        onMouseEnter={() => {
          cancelClose();
          handleOpenChange(true);
        }}
        onMouseLeave={scheduleClose}
      >
        {label}
        <ChevronRight
          size={20}
          className={cn('transition-transform duration-300', open && 'rotate-90')}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="bg-primary text-white !w-auto min-w-[240px] px-5 py-5 rounded-none border-none ring-0 shadow-[0_12px_24px_-8px_rgba(0,0,0,0.45)]"
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClose}
        sideOffset={0}
      >
        {options.map(item => (
          <DropdownMenuItem
            key={item.label}
            onClick={() => {
              handleOpenChange(false);
              item.action();
            }}
            className="w-full cursor-pointer capitalize font-semibold text-sm text-white leading-8 py-1 bg-transparent hover:bg-white/10 focus:bg-white/10 focus:text-white hover:text-secondary border-b border-white/20 last:border-b-0 rounded-none"
          >
            {item.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default MenuButton;
